import { useState } from "react"

import { useTranslation } from "react-i18next"

import type { AlertConfig } from "@/api/alerts-api"

import { Clock, Plus, Save, Trash2 } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"

import { useUpdateAlertConfigMutation } from "../../hooks/admin-queries"

type Props = {
  config: AlertConfig
}

type CooldownEntry = {
  key: string
  seconds: number
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`
  return `${Math.floor(seconds / 86400)}d`
}

export function AlertsCooldownsConfig({ config }: Props) {
  const { t } = useTranslation()
  const updateMutation = useUpdateAlertConfigMutation()

  const [cooldowns, setCooldowns] = useState<CooldownEntry[]>(() =>
    Object.entries(config.cooldowns).map(([key, seconds]) => ({
      key,
      seconds,
    }))
  )
  const [newKey, setNewKey] = useState("")
  const [newSeconds, setNewSeconds] = useState("3600")

  const handleAddCooldown = () => {
    if (!newKey.trim()) return
    if (cooldowns.some((c) => c.key === newKey)) return

    setCooldowns([...cooldowns, { key: newKey.trim(), seconds: parseInt(newSeconds) || 3600 }])
    setNewKey("")
    setNewSeconds("3600")
  }

  const handleRemoveCooldown = (key: string) => {
    setCooldowns(cooldowns.filter((c) => c.key !== key))
  }

  const handleUpdateSeconds = (key: string, seconds: number) => {
    setCooldowns(cooldowns.map((c) => (c.key === key ? { ...c, seconds } : c)))
  }

  const handleSave = async () => {
    const cooldownsObj: Record<string, number> = {}
    for (const entry of cooldowns) {
      cooldownsObj[entry.key] = entry.seconds
    }
    await updateMutation.mutateAsync({ cooldowns: cooldownsObj })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="size-5" />
          {t("adminDashboard.alerts.cooldownSettings", "Cooldown Settings")}
        </CardTitle>
        <CardDescription>
          {t(
            "adminDashboard.alerts.cooldownDesc",
            "Configure minimum time between repeated alerts of the same type"
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border">
          <div className="bg-muted/50 hidden grid-cols-12 gap-4 border-b px-4 py-2 text-sm font-medium lg:grid">
            <div className="col-span-5">
              {t("adminDashboard.alerts.cooldownKey", "Alert Type / Severity")}
            </div>
            <div className="col-span-3">
              {t("adminDashboard.alerts.cooldownSeconds", "Cooldown (seconds)")}
            </div>
            <div className="col-span-2">{t("adminDashboard.alerts.duration", "Duration")}</div>
            <div className="col-span-2 text-right">{t("common.actions", "Actions")}</div>
          </div>

          {cooldowns.length === 0 ? (
            <div className="text-muted-foreground px-4 py-8 text-center">
              {t("adminDashboard.alerts.noCooldowns", "No cooldowns configured")}
            </div>
          ) : (
            cooldowns.map((entry) => (
              <div
                key={entry.key}
                className="admin-responsive-grid-row grid grid-cols-12 items-center gap-4 border-b px-4 py-3 last:border-b-0"
              >
                <div className="col-span-5">
                  <Badge variant={entry.key === "default" ? "default" : "outline"}>
                    {entry.key}
                  </Badge>
                </div>
                <div className="col-span-3">
                  <span className="text-muted-foreground mb-1 block text-xs font-medium lg:hidden">
                    Cooldown seconds
                  </span>
                  <Input
                    aria-label={`${entry.key} cooldown seconds`}
                    className="w-32"
                    min={0}
                    type="number"
                    value={entry.seconds}
                    onChange={(e) => handleUpdateSeconds(entry.key, parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="text-muted-foreground col-span-2">
                  <span className="text-foreground mr-3 text-xs font-medium lg:hidden">
                    Duration
                  </span>
                  {formatDuration(entry.seconds)}
                </div>
                <div className="col-span-2 flex justify-end">
                  <span className="text-muted-foreground mr-auto text-xs font-medium lg:hidden">
                    Actions
                  </span>
                  {entry.key !== "default" && (
                    <Button
                      aria-label={`Remove ${entry.key} cooldown`}
                      size="icon"
                      variant="ghost"
                      onClick={() => handleRemoveCooldown(entry.key)}
                    >
                      <Trash2 className="text-destructive size-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add new cooldown */}
        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          <Input
            className="w-full sm:max-w-xs"
            placeholder="docint_quota_exceeded or severity:critical"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
          />
          <Input
            className="w-full sm:w-32"
            min={0}
            placeholder="3600"
            type="number"
            value={newSeconds}
            onChange={(e) => setNewSeconds(e.target.value)}
          />
          <Button size="sm" variant="outline" onClick={handleAddCooldown}>
            <Plus className="mr-2 size-4" />
            {t("adminDashboard.alerts.addCooldown", "Add")}
          </Button>
        </div>

        <div className="text-muted-foreground text-sm">
          <p>
            {t(
              "adminDashboard.alerts.cooldownHelp",
              "Common values: 3600 (1 hour), 14400 (4 hours), 86400 (24 hours)"
            )}
          </p>
        </div>

        <Button className="w-full" disabled={updateMutation.isPending} onClick={handleSave}>
          <Save className="mr-2 size-4" />
          {t("common.save", "Save Cooldowns")}
        </Button>
      </CardContent>
    </Card>
  )
}
